import React, { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import NtactLogo from '../assets/NTACT Logo.svg';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import LOgo from '../assets/Ntact.png'
import api from '../api/api';
import { Backdrop, CircularProgress } from '@mui/material';
import { isUploadedRef, toDisplayUrl } from './draftImageUtils';




const styles = StyleSheet.create({
  page: {
    padding: 18,
    fontSize: 8,
    fontFamily: 'Helvetica',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  logo: { width: 60, height: 30 },
  title: { fontSize: 14, fontWeight: 'bold', textAlign: 'center' },

  // ==== DOC META ROW ====
  docGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 5,
    marginBottom: 5,
  },
  docItem: {
    border: '0.5px solid #ccc',
    padding: 4,
    borderRadius: 3,
    flex: 1,
    fontSize: 7.5,
  },

  cardTitleBar: {
    backgroundColor: '#000',
    color: '#fff',
    textAlign: 'center',
    paddingVertical: 4,
    marginBottom: 5,
    fontWeight: 'bold',
    fontSize: 11,
  },

  // ==== INFO GRID ====
  infoGrid: {
    flexDirection: 'row',
    gap: 5,
    marginBottom: 5,
  },
  infoItem: {
    border: '0.5px solid #ccc',
    padding: 4,
    borderRadius: 3,
    flex: 1,
    fontSize: 7.5,
  },

  signature: {
    width: 70,
    height: 20,
    border: '0.5px solid #ccc',
    borderRadius: 3,
    objectFit: 'contain',
    marginTop: 3,
  },

  // ==== RADIO BUTTONS ====
  radioContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'nowrap',
    marginTop: 2,
    gap: 4,
  },
  radioButton: {
    width: 7,
    height: 7,
    borderRadius: 4,
    border: '1px solid #555',
    marginLeft: 1,
  },
  radioButtonSmall: {
    width: 8,
    height: 8,
    borderRadius: 4,
    border: '0.75px solid #555',
  },
  radioButtonSelectedS: { backgroundColor: '#28a745' },
  radioButtonSelectedC: { backgroundColor: '#dc3545' },
  radioButtonSelectedNA: { backgroundColor: '#6b7280' },
  radioText: { fontSize: 6.5, marginLeft: 1, marginRight: 2 },

  // ==== INSTRUCTIONS ====
  note: { border: '0.5px solid #ccc', padding: 4, marginBottom: 0, borderRadius: 3, fontSize: 6.5, lineHeight: 1.3 },

  // ==== CRITICAL FACTORS ====
  criticalFactorsHeader: {
    backgroundColor: '#000',
    color: '#fff',
    textAlign: 'center',
    paddingVertical: 4,
    marginBottom: 5,
    fontWeight: 'bold',
    fontSize: 9,
  },
  criticalColumnsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 5,
    marginBottom: 5,
  },
  criticalColumn: {
    flex: 1,
    border: '0.5px solid #ccc',
    borderRadius: 3,
    backgroundColor: '#fff',
  },
  criticalColumnTitle: {
    textAlign: 'center',
    backgroundColor: '#f0f0f0',
    paddingVertical: 5,
    fontWeight: 'bold',
    fontSize: 9.5,
    borderBottom: '0.5px solid #ccc',
  },
  criticalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderBottom: '0.5px solid #eee',
  },
  criticalRowText: { fontSize: 8.5, textTransform: 'uppercase', flex: 1, marginRight: 6 },
  criticalRadioGroup: { flexDirection: 'row', alignItems: 'center', gap: 6 },

  // ==== FEEDBACK ====
  // Sized to its own content (no flexGrow) so a short answer gets a short
  // box instead of stretching to fill the rest of the page.
  feedbackBox: {
    border: '0.5px solid #ccc',
    padding: 6,
    borderRadius: 3,
    marginTop: 2,
  },
  feedbackTitle: { fontWeight: 'bold', fontSize: 8.5, marginBottom: 3 },
  feedbackText: { fontSize: 7.5, lineHeight: 1.35 },
});

// Long free-text answers (feedback, task description) would otherwise wrap
// onto extra lines and push the single-page PDF into a second page — shrink
// font/line-height as the text grows so longer answers stay on page 1.
const getAutoShrinkStyle = (text, { base, min, charsPerStep, lineHeightBase }) => {
  const len = (text || '').length;
  const fontSize = Math.max(min, base - Math.floor(len / charsPerStep));
  const lineHeight = Math.max(1.05, lineHeightBase - (base - fontSize) * 0.04);
  return { fontSize, lineHeight };
};

const getFeedbackTextStyle = (text) =>
  getAutoShrinkStyle(text, { base: 7.5, min: 4, charsPerStep: 100, lineHeightBase: 1.35 });

const getTaskTextStyle = (text) =>
  getAutoShrinkStyle(text, { base: 7.5, min: 5, charsPerStep: 130, lineHeightBase: 1.3 });

const PDFDocument = ({
  observerName,
  clientobservationsign,
  filledDate,
  empName,
  empId,
  jobDescription,
  jobId,
  taskPerformed,
  stopWorkAuthority,
  clientJointAnswer,
  feedback,
  ppeItems,
  bodyPositionItems,
  workEnvironmentItems,
  workProcedureItems
}) => (
  <Document>
    <Page size="A4" orientation="portrait" style={styles.page}>
      <View style={styles.header}>
        <Image src={LOgo} style={styles.logo} />
        <Text style={styles.title}>HEALTH SAFETY & ENVIRONMENTAL</Text>
        <View style={{ width: 50 }} />
      </View>

      <View style={styles.docGrid}>
        <View style={styles.docItem}>
          <Text style={{ fontWeight: 'bold' }}>Document Name:</Text>
          <Text>BBS SAFETY OBSERVATION FORM</Text>
        </View>
        <View style={styles.docItem}>
          <Text style={{ fontWeight: 'bold' }}>Section:</Text>
          <Text>HSE-7</Text>
        </View>
        <View style={styles.docItem}>
          <Text style={{ fontWeight: 'bold' }}>Revision:</Text>
          <Text>1</Text>
        </View>
        <View style={styles.docItem}>
          <Text style={{ fontWeight: 'bold' }}>Date:</Text>
          <Text>{filledDate}</Text>
        </View>
      </View>

      <Text style={styles.cardTitleBar}>BEHAVIOR BASED SAFETY (BBS) CARD</Text>

      <View style={styles.infoGrid}>
        {observerName && (
          <View style={styles.infoItem}>
            <Text style={{ fontWeight: 'bold' }}>Observer Name:</Text>
            <Text>{observerName}</Text>
          </View>
        )}
        {empName && empName !== "N/A" && (
          <View style={styles.infoItem}>
            <Text style={{ fontWeight: 'bold' }}>Employee:</Text>
            <Text>{empName} ({empId})</Text>
          </View>
        )}
        <View style={styles.infoItem}>
          <Text style={{ fontWeight: 'bold' }}>Job:</Text>
          <Text>{jobDescription} #{jobId}</Text>
        </View>
      </View>

      <View style={styles.infoGrid}>
        <View style={[styles.infoItem, { flex: 1 }]}>
          <Text style={{ fontWeight: 'bold' }}>SWA Used?</Text>
          <View style={styles.radioContainer}>
            <Text style={styles.radioText}>YES</Text>
            <View style={[styles.radioButton, stopWorkAuthority === 'YES' && styles.radioButtonSelectedS]} />
            <Text style={styles.radioText}>NO</Text>
            <View style={[styles.radioButton, stopWorkAuthority === 'NO' && styles.radioButtonSelectedC]} />
          </View>
        </View>
        <View style={[styles.infoItem, { flex: 1 }]}>
          <Text style={{ fontWeight: 'bold' }}>Client Joint Observation?</Text>
          <View style={styles.radioContainer}>
            <Text style={styles.radioText}>YES</Text>
            <View style={[styles.radioButton, clientJointAnswer === 'YES' && styles.radioButtonSelectedS]} />
            <Text style={styles.radioText}>NO</Text>
            <View style={[styles.radioButton, clientJointAnswer === 'NO' && styles.radioButtonSelectedC]} />
          </View>
          {clientJointAnswer === 'YES' && clientobservationsign?.base64_data && (
            <Image src={clientobservationsign.base64_data} style={styles.signature} />
          )}
        </View>
      </View>

      <View style={styles.infoGrid}>
        <View style={[styles.infoItem, { flex: 2 }]}>
          <Text style={{ fontWeight: 'bold' }}>What task(s) were employee(s) performing?</Text>
          <Text style={getTaskTextStyle(taskPerformed)}>{taskPerformed}</Text>
        </View>
        <View style={[styles.note, { flex: 3, marginBottom: 0 }]}>
          <Text style={{ fontWeight: 'bold', marginBottom: 1 }}>BBS INSTRUCTIONS:</Text>
          <Text>1) Inform co-worker(s) of the observation. 2) Observe task, focusing on Critical Factors. 3) Give feedback and forward this form to the Safety Department.</Text>
        </View>
      </View>

      <Text style={styles.criticalFactorsHeader}>CRITICAL FACTORS — S = SAFE, C = CONCERN, GRAY = N/A</Text>
      {[
        [
          { title: '1. PPE', items: ppeItems, strip: 'PPE ' },
          { title: '2. Body Position', items: bodyPositionItems, strip: 'Body Position - ' },
        ],
        [
          { title: '3. Work Environment', items: workEnvironmentItems, strip: 'Work Environment - ' },
          { title: '4. Work Procedure', items: workProcedureItems, strip: 'Work Procedure - ' },
        ],
      ].map((row, rIndex) => (
        <View key={rIndex} style={styles.criticalColumnsRow}>
          {row.map((section, sIndex) => (
            <View key={sIndex} style={styles.criticalColumn}>
              <Text style={styles.criticalColumnTitle}>{section.title}</Text>
              {section.items.map((item, i) => (
                <View key={i} style={styles.criticalRow}>
                  <Text style={styles.criticalRowText}>{item.question.replace(section.strip, "")}</Text>
                  <View style={styles.criticalRadioGroup}>
                    <View style={[styles.radioButtonSmall, item.answer === "S" && styles.radioButtonSelectedS]} />
                    <View style={[styles.radioButtonSmall, item.answer === "C" && styles.radioButtonSelectedC]} />
                    <View style={[styles.radioButtonSmall, item.answer !== "S" && item.answer !== "C" && styles.radioButtonSelectedNA]} />
                  </View>
                </View>
              ))}
            </View>
          ))}
        </View>
      ))}

      <View style={styles.feedbackBox}>
        <Text style={styles.feedbackTitle}>IMPROVEMENT INPUT — Observer's Feedback (Action Taken or Recommendation):</Text>
        <Text style={[styles.feedbackText, getFeedbackTextStyle(feedback)]}>{feedback}</Text>
      </View>
    </Page>
  </Document>
);



const ViewFormModal = ({ setFormView, rowData, formType, onEdit }) => {
  const formItems = rowData?.json_data || [];
   const formRef = useRef(null);
// formItems.imagekey={
//                             "file_name": "download (10)_20251205091809.jpg",
//                             "file_path": "/var/www/hub2.ntactops.com/ntact/public/assets/forms_files/download (10)_20251205091809.jpg"
//                         }
  const [clientobservationsign,setClientobservationsign]=useState(null)
    const [ocrimg,setOcrimg]=useState(null)
    const [loading, setLoading] = useState(false);
  // Extract specific fields for the header and other sections
  console.log(formItems)
  const observerName =rowData?.observer_name
  const filledDate = rowData?.filled_date || "N/A";
  const taskPerformed = formItems.find(item => item.question?.includes("What task(s) were employee(s) performing"))?.answer || "N/A";
  const stopWorkAuthority = formItems.find(item => item.question?.includes("Stop Work Authority"))?.answer || "N/A";
  // Client Joint Observation Yes/No answer (question ends with "?"). Falls back
  // to legacy records that only had a signature: if a signature object exists,
  // treat it as YES.
  const clientJointAnswerRaw = formItems.find(item => item.question === "Client Joint Observation?")?.answer;
  const clientJointSigItem = formItems.find(item =>
    item.question === "Client Joint Observation" &&
    item.answer && typeof item.answer === "object" && item.answer.file_path
  );
  const clientJointAnswer = clientJointAnswerRaw || (clientJointSigItem ? "YES" : "NO");
  const feedback = formItems.find(item => item.question?.includes("Observer's feedback"))?.answer || "N/A";

  // Filter items into categories based on question prefixes
  const ppeItems = formItems.filter(item => item.question?.startsWith("PPE"));
  const bodyPositionItems = formItems.filter(item => item.question?.startsWith("Body Position"));
  const workEnvironmentItems = formItems.filter(item => item.question?.startsWith("Work Environment"));
  const workProcedureItems = formItems.filter(item => item.question?.startsWith("Work Procedure"));
  const [view,setView]=useState(0)
const fetchClientObservationImage = async () => {
  try {
    const clientJointObservation = formItems.find(item =>
      item.question === "Client Joint Observation" &&
      item.answer && typeof item.answer === "object" && (item.answer.file_path || item.answer.file_name)
    )?.answer;

    if (!clientJointObservation || typeof clientJointObservation !== "object") {
      return null;
    }

    // New draft format: uploaded ref with displayUrl or file_name — display directly
    if (isUploadedRef(clientJointObservation)) {
      const url = clientJointObservation.displayUrl || toDisplayUrl(clientJointObservation);
      if (url) {
        setClientobservationsign({ base64_data: url });
        return url;
      }
    }

    const fileName = clientJointObservation?.file_name;
    if (!fileName) {
      return null;
    }

    // Legacy path: fetch base64 via API
    const res = await api.get(
      `/v1/documents/view-multiple?filenames=${encodeURIComponent(fileName)}`
    );

    if (res.status === 200) {
      const imageUrl = res.data?.images?.[0] || res.images?.[0] || null;
      if (imageUrl) {
        setClientobservationsign(imageUrl);
        return imageUrl;
      }
    }
    return null;
  } catch (error) {
    console.error("Error fetching Client Observation image:", error);
    return null;
  }
};
const fetchOcrImage = async (fileName) => {
  try {
setLoading(true)
    // Call API
    const res = await api.get(
      `/v1/documents/view-multiple?filenames=${encodeURIComponent(fileName)}`
    );


    if (res.status === 200) {
      const imageUrl = res.data?.images?.[0] || res.images?.[0] || null;
      if (imageUrl) {
        console.log("🖼️ Client Observation Image URL:", imageUrl);
        setOcrimg(imageUrl)
        return imageUrl;
      } else {
        console.warn("⚠️ No image URL found in response.");
        return null;
      }
    } else {
      console.warn("⚠️ Non-200 API status:", res.status);
      return null;
    }
  } catch (error) {
    console.error("❌ Error fetching Client Observation image:", error);
    return null;
  }finally{
    setLoading(false)
  }
};
useEffect(() => {
    const getClientObservation = async () => {
      setLoading(true);
      fetchClientObservationImage()
    
      setLoading(false);
    };

    getClientObservation();
  }, [formItems]);

  const generatePDF = async () => {
    try {
      const { pdf } = await import('@react-pdf/renderer');
      const blob = await pdf(
        <PDFDocument
          observerName={observerName}
          filledDate={filledDate}
          empName={rowData?.emp_name || "N/A"}
          empId={rowData?.emp_id || "N/A"}
          jobDescription={rowData?.jobDescription || "N/A"}
          jobId={rowData?.job_id || "N/A"}
          taskPerformed={taskPerformed}
          stopWorkAuthority={stopWorkAuthority}
          clientJointAnswer={clientJointAnswer}
          feedback={feedback}
          ppeItems={ppeItems}
          bodyPositionItems={bodyPositionItems}
          workEnvironmentItems={workEnvironmentItems}
          workProcedureItems={workProcedureItems}
          clientobservationsign={clientobservationsign}
        />
      ).toBlob();
      const pdfUrl = URL.createObjectURL(blob);
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };


   useEffect(() => {
      if (formRef.current) {
        formRef.current.scrollTop = 0;
      }
    }, []);
  
    const handleBackdropClick = (e) => {
      // Check if the click target is the backdrop itself (not the modal content)
      if (e.target === e.currentTarget) {
        setFormView(false);
      }
    };

  return (
    <div onClick={handleBackdropClick} className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40  ">
      <div className="relative w-full max-w-4xl flex flex-col justify-center items-center p-6 bg-white rounded-lg border border-gray-300 shadow-md overflow-y-auto custom-scrollbar max-h-[95vh]">
    <div className="w-full flex justify-between items-end">
  <div className="flex gap-4">

    {/* PRINT BUTTON OR PLACEHOLDER */}
    {view === 0 ? (
      <button
        onClick={generatePDF}
        className="bg-red-800 text-white px-4 py-1 rounded hover:bg-green-600"
      >
        Print
      </button>
    ) : (
     <></>
    )}

    {/* EDIT BUTTON — only rendered when the parent passes `onEdit`. Hidden on
        the secondary OCR-image view so it doesn't compete with the back
        button. */}
    {view === 0 && typeof onEdit === "function" && (
      <button
        onClick={onEdit}
        className="bg-[#1E4976] text-white px-4 py-1 rounded hover:bg-[#163659]"
      >
        Edit
      </button>
    )}

    {/* UPLOADED IMAGE BUTTON */}
    {/* {formItems?.some(item => item?.capture_form_image?.file_name) && (
      <button
        onClick={() => {
          if (view === 0) {
            const fileName =
              formItems.find(item => item?.capture_form_image?.file_name)
                ?.capture_form_image?.file_name;

            if (fileName) fetchOcrImage(fileName);
          }

          setView(view === 0 ? 1 : 0);
        }}
        className="bg-red-800 text-white px-4 py-1 rounded hover:bg-green-600"
      >
        {view === 1 ? "Back To Form" : "View Uploaded Image"}
      </button>
    )} */}
  </div>

  <button onClick={() => setFormView(false)} className="text-gray-600 hover:text-black">
    <CloseIcon />
  </button>
</div>


    
        {view==0?<div ref={formRef} className="w-[90%] mx-auto  overflow-y-auto custom-scrollbar p-4">
          <div className="border-b border-gray-300 pb-2 mb-4">
            <div className='flex w-full justify-between'>
              <img src={NtactLogo} alt='$/'/>
            <div>
                <h2 className="text-lg font-semibold">HEALTH SAFETY & ENVIRONMENTAL</h2>
              </div>
              <div className='mr-24'>

              </div>
            </div>
            <div className="flex justify-between items-center">
              <div>
              <p className="text-sm font-semibold text-left mt-2">Document Name:<br>
              </br> BBS SAFETY OBSERVATION FORM</p>
          
              </div>
              <div className="text-right ">
              
                <p className="text-sm font-semibold text-left">Section: <br>
                </br>HSE-7</p>
          
              </div>
              <div className="text-right">
              
                <p className="text-sm font-semibold">Revision: 1</p>
                <p className="text-sm font-semibold">Date: {filledDate}</p>
             
                <p className="text-sm font-semibold">Document Number: 7-5-A</p>

              </div>
            </div>
          </div>

          {/* Modal Header */}
          <div className="flex items-center justify-center w-full border-b border-gray-300 pb-2 mb-4">
            <h2 className="text-lg font-bold">BEHAVIOR BASED SAFETY (BBS) CARD</h2>
          
          </div>

          {/* Observer and Date Info */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {observerName?<div className=' border border-[#9CA3AF] p-1'>
              <p className="text-sm font-inter font-semibold text-left">Observer Name:</p>
              <p className="text-left font-inter p-1 px-2 ">{observerName}</p>
              <hr className="border-t border-[#9CA3AF]" />
              </div>:<></>}
            <div className=' border border-[#9CA3AF] p-1'>
              <p className="text-sm font-inter font-semibold text-left">Date:</p>
              <p className="text-left font-inter p-1 px-2 ">{filledDate}</p>
              <hr className="border-t border-[#9CA3AF]" />
            </div>
            {rowData?.emp_name?<div className=' border border-[#9CA3AF] p-1'>
              <p className="text-sm font-inter font-semibold text-left">Employee:</p>
              <p className="text-left font-inter p-1 px-2 ">{rowData?.emp_name} ({rowData?.emp_id})</p>
              <hr className="border-t border-[#9CA3AF]" />
            </div>:<></>}
            <div className=' border border-[#9CA3AF] p-1'>
              <p className="text-sm font-inter font-semibold text-left">Job:</p>
              <p className="text-left font-inter p-1 px-2">{rowData?.job_description} #{rowData?.job_id}</p>
              <hr className="border-t border-[#9CA3AF]" />

            </div>
          </div>

          {/* Stop Work Authority and Client Joint Observation */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className=' border border-[#9CA3AF] p-1'>
              <p className="text-sm font-inter font-semibold text-left">Stop Work Authority (SWA) Used?</p>
              <div className="flex space-x-4 mt-1">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="swa"
                    value="YES"
                    checked={stopWorkAuthority === "YES"}
                  
                    className="mr-1 accent-green-600"
                  />
                  YES
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="swa"
                    value="NO"
                    checked={stopWorkAuthority === "NO"}
                  
                    className="mr-1 accent-red-600"
                  />
                  NO
                </label>
              </div>
            </div>
            <div className=' border border-[#9CA3AF] p-1'>
              <p className="text-sm font-inter font-semibold text-left">Client Joint Observation?</p>
              <div className="flex space-x-4 mt-1">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="cjo"
                    value="YES"
                    checked={clientJointAnswer === "YES"}
                    readOnly
                    className="mr-1 accent-green-600"
                  />
                  YES
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="cjo"
                    value="NO"
                    checked={clientJointAnswer === "NO"}
                    readOnly
                    className="mr-1 accent-red-600"
                  />
                  NO
                </label>
              </div>

              {clientJointAnswer === "YES" && (
                <div className="mt-4">
                  <p className="text-xs font-inter font-semibold text-left text-gray-600 mb-1">
                    Client Joint Observation Signature:
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <div className="border border-gray-300 rounded overflow-hidden">
                      {clientobservationsign?.base64_data ? (
                        <img
                          src={clientobservationsign?.base64_data}
                          alt="Signature"
                          style={{
                            width: '160px',
                            height: '40px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            objectFit: 'contain',
                          }}
                        />
                      ) : (
                        <div
                          className="flex items-center justify-center text-xs text-gray-400"
                          style={{ width: '160px', height: '40px' }}
                        >
                          No signature
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <hr className="border-t border-[#9CA3AF]" />
            </div>
          </div>

          {/* Task Performed */}
          <div  className=' border border-[#9CA3AF] p-1 mb-4'>
            <p className="text-sm font-inter font-semibold text-left">What task(s) were employee(s) performing during the BBS observation?</p>
            <p className="text-sm  text-gray-700 text-left font-inter mt-4 py-2 px-3">{taskPerformed}</p>
            <hr className="border-t border-[#9CA3AF]" />

          </div>

          {/* BBS Instructions */}
          <div className="mb-4 p-4 border border-gray-300 rounded">
            <p className="text-sm font-inter font-semibold text-left mb-2">BBS INSTRUCTIONS:</p>
            <p className="text-sm font-inter text-left">1) Inform co-worker(s) that you are going to perform a BBS observation on the task they are performing.</p>
            <p className="text-sm font-inter text-left">2) Observe co-worker(s) performing task while paying attention to the Critical Factors Section.</p>
            <p className="text-sm font-inter text-left">3) After the observations provide feedback to co-worker(s) whether safe or concerning, some categories may not apply and are to be left blank. Promptly, after observations give this form to your supervisor who will review it and who must then forward it to the Safety Department.</p>
          </div>

          {/* Feedback */}
        

          {/* Critical Factors Header */}
          <div className="mb-4 w-full flex justify-center  p-2">
  <p className="text-sm font-inter text-center font-bold">
    CRITICAL FACTORS S=SAFE C=CONCERN
  </p>
</div>


          {/* Critical Factors Sections */}
          <div className="grid grid-cols-2 gap-4 pr-2">
            {/* PPE */}
            <div className='border'>
              <p className="text-sm font-inter font-semibold text-center border-b pb-1 mb-2 bg-gray-200 p-2">1. PPE</p>
       {ppeItems.map((item, index) => {
  const ans = item.answer;
  const isS = ans === "S";
  const isC = ans === "C";
  const isNA = !isS && !isC; // anything else or empty

  return (
    <div key={index} className="flex justify-between items-center px-2 border-b">
      <p className="text-sm font-inter text-left">
        {item.question.replace("PPE ", "")}
      </p>

      <div className="flex space-x-3">
        {/* S */}
        <label className="flex items-center">
          <input
            type="radio"
            name={`ppe-${index}`}
            value="S"
            checked={isS}
            readOnly
            className={`mr-1 ${isS ? "accent-green-600" : ""}`}
          />
          <span className={isS ? "text-green-600 font-bold" : "text-gray-400"}>S</span>
        </label>

        {/* C */}
        <label className="flex items-center">
          <input
            type="radio"
            name={`ppe-${index}`}
            value="C"
            checked={isC}
            readOnly
            className={`mr-1 ${isC ? "accent-red-600" : ""}`}
          />
          <span className={isC ? "text-red-600 font-bold" : "text-gray-400"}>C</span>
        </label>

        {/* N/A */}
        <label className="flex items-center">
          <input
            type="radio"
            name={`ppe-${index}`}
            value="NA"
            checked={isNA}
            readOnly
            className={`mr-1 ${isNA ? "accent-gray-500" : ""}`}
          />
          <span className={isNA ? "text-gray-600 font-bold" : "text-gray-400"}>
            N/A
          </span>
        </label>
      </div>
    </div>
  );
})}
            </div>

            {/* Body Position */}
            <div className='border'>
              <p className="text-sm font-inter font-semibold text-center border-b pb-1 mb-2 bg-gray-200 p-2">2. Body Position</p>
          {bodyPositionItems.map((item, index) => {
  const ans = item.answer;
  const isS = ans === "S";
  const isC = ans === "C";
  const isNA = !isS && !isC;

  return (
    <div key={index} className="flex justify-between items-center px-2 border-b my-3">
      <p className="text-sm font-inter text-left">
        {item.question.replace("Body Position - ", "")}
      </p>

      <div className="flex space-x-3">
        {/* S */}
        <label className="flex items-center">
          <input
            type="radio"
            name={`body-position-${index}`}
            value="S"
            checked={isS}
            readOnly
            className={`mr-1 ${isS ? "accent-green-600" : ""}`}
          />
          <span className={isS ? "text-green-600 font-bold" : "text-gray-400"}>S</span>
        </label>

        {/* C */}
        <label className="flex items-center">
          <input
            type="radio"
            name={`body-position-${index}`}
            value="C"
            checked={isC}
            readOnly
            className={`mr-1 ${isC ? "accent-red-600" : ""}`}
          />
          <span className={isC ? "text-red-600 font-bold" : "text-gray-400"}>C</span>
        </label>

        {/* N/A */}
        <label className="flex items-center">
          <input
            type="radio"
            name={`body-position-${index}`}
            value="NA"
            checked={isNA}
            readOnly
            className={`mr-1 ${isNA ? "accent-gray-500" : ""}`}
          />
          <span className={isNA ? "text-gray-600 font-bold" : "text-gray-400"}>
            N/A
          </span>
        </label>
      </div>
    </div>
  );
})}
            </div>

            {/* Work Environment */}
            <div className='border'>
              <p className="text-sm font-inter font-semibold text-center bg-gray-200 p-2 border-b pb-1 mb-2">3. Work Environment</p>
            {workEnvironmentItems.map((item, index) => {
  const ans = item.answer;
  const isS = ans === "S";
  const isC = ans === "C";
  const isNA = !isS && !isC;

  return (
    <div key={index} className="flex justify-between items-center px-2 border-b my-3">
      <p className="text-sm font-inter text-left">
        {item.question.replace("Work Environment - ", "")}
      </p>

      <div className="flex space-x-3">
        {/* S */}
        <label className="flex items-center">
          <input
            type="radio"
            name={`work-env-${index}`}
            value="S"
            checked={isS}
            readOnly
            className={`mr-1 ${isS ? "accent-green-600" : ""}`}
          />
          <span className={isS ? "text-green-600 font-bold" : "text-gray-400"}>S</span>
        </label>

        {/* C */}
        <label className="flex items-center">
          <input
            type="radio"
            name={`work-env-${index}`}
            value="C"
            checked={isC}
            readOnly
            className={`mr-1 ${isC ? "accent-red-600" : ""}`}
          />
          <span className={isC ? "text-red-600 font-bold" : "text-gray-400"}>C</span>
        </label>

        {/* N/A */}
        <label className="flex items-center">
          <input
            type="radio"
            name={`work-env-${index}`}
            value="NA"
            checked={isNA}
            readOnly
            className={`mr-1 ${isNA ? "accent-gray-500" : ""}`}
          />
          <span className={isNA ? "text-gray-600 font-bold" : "text-gray-400"}>
            N/A
          </span>
        </label>
      </div>
    </div>
  );
})}
            </div>

            {/* Work Procedure */}
            <div className='border'>
              <p className="text-sm font-inter font-semibold bg-gray-200 p-2 text-center border-b pb-1 mb-2">4. Work Procedure</p>
            {workProcedureItems.map((item, index) => {
  const ans = item.answer;
  const isS = ans === "S";
  const isC = ans === "C";
  const isNA = !isS && !isC;

  return (
    <div key={index} className="flex justify-between items-center px-2 border-b my-3">
      <p className="text-sm font-inter text-left">
        {item.question.replace("Work Procedure - ", "")}
      </p>

      <div className="flex space-x-3">
        {/* S */}
        <label className="flex items-center">
          <input
            type="radio"
            name={`work-procedure-${index}`}
            value="S"
            checked={isS}
            readOnly
            className={`mr-1 ${isS ? "accent-green-600" : ""}`}
          />
          <span className={isS ? "text-green-600 font-bold" : "text-gray-400"}>S</span>
        </label>

        {/* C */}
        <label className="flex items-center">
          <input
            type="radio"
            name={`work-procedure-${index}`}
            value="C"
            checked={isC}
            readOnly
            className={`mr-1 ${isC ? "accent-red-600" : ""}`}
          />
          <span className={isC ? "text-red-600 font-bold" : "text-gray-400"}>C</span>
        </label>

        {/* N/A */}
        <label className="flex items-center">
          <input
            type="radio"
            name={`work-procedure-${index}`}
            value="NA"
            checked={isNA}
            readOnly
            className={`mr-1 ${isNA ? "accent-gray-500" : ""}`}
          />
          <span className={isNA ? "text-gray-600 font-bold" : "text-gray-400"}>
            N/A
          </span>
        </label>
      </div>
    </div>
  );
})}
            </div>
          </div>
          <div className="mb-4">
            <div className='text-[16px] my-4 text-left font-semibold'>IMPROVEMENT INPUT</div>
            <div className=' border border-[#9CA3AF] p-1'>
            <p className="text-sm font-inter font-semibold text-left">Observer's Feedback (Action Taken or Recommendation):</p>
            <p className="text-sm text-wrap text-gray-700 text-left font-inter py-2 px-3">{feedback}</p>
            <hr className="border-t border-[#9CA3AF]" />

            </div>

          </div>
        </div>:<>
        
        <div className="w-[90%] mx-auto  overflow-y-auto custom-scrollbar p-4">

  {ocrimg?.base64_data&& (
  <img
    src={ocrimg?.base64_data}
    alt="Captured Image"
    className="rounded"
    style={{ maxWidth: "100%", height: "auto" }}
    
  />
)}

        </div>
      

</>}
      </div>
              <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default ViewFormModal;